import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  BarChart3,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Minus,
  FileDown,
  ChevronRight,
  Info,
  Target,
  Activity,
  Zap
} from 'lucide-react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface Stakeholder {
  id: string;
  name: string;
  ownership: number;
  impact: {
    scenario1: 'neutral' | 'positive' | 'negative';
    scenario2: 'neutral' | 'positive' | 'negative';
    scenario3: 'neutral' | 'positive' | 'negative';
    scenario4: 'neutral' | 'positive' | 'negative';
  };
  impactStrength: {
    scenario1: number; // -3 to +3
    scenario2: number;
    scenario3: number;
    scenario4: number;
  };
}

interface Scenario {
  id: string;
  title: string;
  description: string;
  objective: string;
  implications: string[];
  advantages: string[];
  disadvantages: string[];
  risks: string[];
  winners: string[];
  losers: string[];
}

interface CompensationMechanism {
  id: string;
  title: string;
  description: string;
  pros: string[];
  cons: string[];
}

// ============================================================================
// DATA CONSTANTS
// ============================================================================

const STAKEHOLDERS: Stakeholder[] = [
  {
    id: 'dany',
    name: 'Dany Bourassa',
    ownership: 31.49,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'negative',
      scenario4: 'negative'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: -3,
      scenario4: -3
    }
  },
  {
    id: 'yannick',
    name: 'Yannick Moreau',
    ownership: 15,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'positive',
      scenario4: 'positive'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: 2,
      scenario4: 2
    }
  },
  {
    id: 'francois',
    name: 'François Delisle',
    ownership: 13.5,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'positive',
      scenario4: 'positive'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: 2,
      scenario4: 2
    }
  },
  {
    id: 'octet',
    name: 'Gestion Octet',
    ownership: 8,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'positive',
      scenario4: 'positive'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: 1,
      scenario4: 1
    }
  },
  {
    id: 'vincent',
    name: 'Vincent Bourassa',
    ownership: 6,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'positive',
      scenario4: 'positive'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: 1,
      scenario4: 1
    }
  },
  {
    id: 'zicat',
    name: 'François Zicat',
    ownership: 5,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'negative',
      scenario4: 'negative'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: -1,
      scenario4: -1
    }
  },
  {
    id: 'jerome',
    name: 'Jérôme Martel',
    ownership: 2,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'positive',
      scenario4: 'positive'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: 1,
      scenario4: 1
    }
  },
  {
    id: 'jp',
    name: 'Jean-Pierre Leclerc',
    ownership: 1,
    impact: {
      scenario1: 'neutral',
      scenario2: 'neutral',
      scenario3: 'positive',
      scenario4: 'positive'
    },
    impactStrength: {
      scenario1: 0,
      scenario2: 0,
      scenario3: 1,
      scenario4: 1
    }
  }
];

const SCENARIOS: Scenario[] = [
  {
    id: 'scenario1',
    title: 'Statut quo',
    description: 'Conserver la structure actuelle sans modifications.',
    objective: 'Maintenir la structure corporative existante sans changements.',
    implications: [
      'Aucune modification de structure',
      'Dette actuelle maintenue (~3.1M)',
      'Placements individuels conservés',
      'Opérations normales continuent'
    ],
    advantages: [
      'Aucun coût de restructuration',
      'Pas de risque fiscal immédiat',
      'Simplicité administrative',
      'Pas de négociation requise'
    ],
    disadvantages: [
      'Pas d\'optimisation fiscale',
      'Dette supportée proportionnellement',
      'Pas de purification AAPE/ECGC',
      'Structure complexe maintenue'
    ],
    risks: [
      'Débalancement si fusion future',
      '55(2) LIR si restructuration ultérieure',
      'Complexité NEQ maintenue',
      'Cash-flow impact continu'
    ],
    winners: ['Tous (neutre)'],
    losers: ['Aucun (neutre)']
  },
  {
    id: 'scenario2',
    title: 'Statut quo + Optimisation individuelle',
    description: 'Conserver la structure mais optimiser individuellement (purification AAPE/ECGC).',
    objective: 'Optimiser fiscalement chaque actionnaire sans changer la structure corporative.',
    implications: [
      'Purification placements vs actions BZ',
      'Séparation pour personnes visées',
      'Validation AAPE/ECGC par individu',
      'Structure corporative inchangée'
    ],
    advantages: [
      'Optimisation fiscale individuelle',
      'Pas de changement structurel majeur',
      'Risque fiscal réduit',
      'Flexibilité par actionnaire'
    ],
    disadvantages: [
      'Coûts de purification',
      'Complexité administrative accrue',
      'Validation requise par individu',
      'Pas de simplification structurelle'
    ],
    risks: [
      'Risque 55(2) LIR si mal exécuté',
      'Validation AAPE/ECGC par cas',
      'Coûts de conseil fiscaux',
      'Timing d\'exécution'
    ],
    winners: ['Tous (optimisation individuelle)'],
    losers: ['Aucun']
  },
  {
    id: 'scenario3',
    title: 'Fusion avec BZ inc. (excluant François Zicat et Dany Bourassa)',
    description: 'Fusion des sociétés de gestion dans BZ inc., excluant Zicat et Dany.',
    objective: 'Simplifier la structure en fusionnant les sociétés de gestion dans BZ inc.',
    implications: [
      'Fusion des sociétés de gestion',
      'Dette mutualisée (~3.1M)',
      'Débalancement pour Dany (31.49% de dette)',
      'Intérêts supportés proportionnellement'
    ],
    advantages: [
      'Simplification structurelle',
      'Réduction complexité NEQ',
      'Coûts admin réduits',
      'Structure plus claire'
    ],
    disadvantages: [
      'Débalancement pour Dany (~36K/an intérêts)',
      'Impact négatif pour Zicat',
      'Négociation requise',
      'Mécanisme compensatoire nécessaire'
    ],
    risks: [
      'Débalancement économique Dany',
      '55(2) LIR si mal structuré',
      'Négociation complexe',
      'Cash-flow impact Dany'
    ],
    winners: ['Yannick Moreau', 'François Delisle', 'Gestion Octet', 'Vincent Bourassa', 'Jérôme Martel', 'Jean-Pierre Leclerc'],
    losers: ['Dany Bourassa', 'François Zicat']
  },
  {
    id: 'scenario4',
    title: 'Société de gestion commune (excluant François Zicat et Dany Bourassa)',
    description: 'Création d\'une société de gestion commune pour les actionnaires, excluant Zicat et Dany.',
    objective: 'Créer une structure de gestion commune simplifiée.',
    implications: [
      'Nouvelle société de gestion',
      'Dette mutualisée (~3.1M)',
      'Débalancement pour Dany',
      'Structure commune pour actionnaires'
    ],
    advantages: [
      'Structure dédiée gestion',
      'Séparation claire des rôles',
      'Flexibilité opérationnelle',
      'Simplification relative'
    ],
    disadvantages: [
      'Débalancement pour Dany',
      'Impact négatif pour Zicat',
      'Coûts de création',
      'Complexité initiale'
    ],
    risks: [
      'Débalancement économique Dany',
      '55(2) LIR',
      'Négociation complexe',
      'Cash-flow impact'
    ],
    winners: ['Yannick Moreau', 'François Delisle', 'Gestion Octet', 'Vincent Bourassa', 'Jérôme Martel', 'Jean-Pierre Leclerc'],
    losers: ['Dany Bourassa', 'François Zicat']
  }
];

const COMPENSATION_MECHANISMS: CompensationMechanism[] = [
  {
    id: 'salary',
    title: 'Augmentation de salaire',
    description: 'Augmenter le salaire de Dany pour compenser l\'impact des intérêts.',
    pros: [
      'Simple à mettre en place',
      'Impact immédiat',
      'Pas de modification structurelle'
    ],
    cons: [
      'Coût récurrent',
      'Impact sur marge',
      'Pas de récupération si vente'
    ]
  },
  {
    id: 'bonus',
    title: 'Bonus indexé aux intérêts',
    description: 'Bonus annuel équivalent aux intérêts supportés par Dany.',
    pros: [
      'Indexé à la dette',
      'Flexible annuellement',
      'Pas de modification permanente'
    ],
    cons: [
      'Décision annuelle requise',
      'Impact sur résultats',
      'Pas garanti'
    ]
  },
  {
    id: 'dividend',
    title: 'Dividende prioritaire',
    description: 'Créer une classe d\'actions préférentielles avec dividende prioritaire pour Dany.',
    pros: [
      'Permanent et garanti',
      'Récupération possible',
      'Structure claire'
    ],
    cons: [
      'Modification structurelle',
      'Complexité juridique',
      'Impact sur autres actionnaires'
    ]
  },
  {
    id: 'neutrality',
    title: 'Clause de neutralité économique',
    description: 'Clause dans convention d\'actionnaires garantissant la neutralité économique pour Dany.',
    pros: [
      'Protection contractuelle',
      'Clarté juridique',
      'Long terme'
    ],
    cons: [
      'Négociation complexe',
      'Rédaction juridique',
      'Validation par tous'
    ]
  }
];

// Assumptions constants (for reference)
// saleYear: 2028
// debt: ~3.1M
// interestRate: 5%
// danyOwnership: 31.49%
// estimatedAnnualInterest: 155K
// danyShareInterest: ~36K/an

const NEXT_STEPS = [
  {
    category: 'Tech',
    items: [
      'Tableau officiel "présentable CE/DFK" + export PDF',
      'Script de discussion calibré (Yannick, Delisle, Octet, etc.)',
      'Calcul précis de compensation (net après impôt) si scénario 3/4'
    ]
  },
  {
    category: 'Gouvernance',
    items: [
      'Clauses type pour convention d\'actionnaires : neutralité économique, dividende prioritaire, mécanisme indexé aux intérêts'
    ]
  },
  {
    category: 'Exécution Scénario 2',
    items: [
      'Purification / séparation placements vs actions BZ pour personnes visées',
      'Check AAPE/ECGC par individu',
      'Validation risques 55(2) LIR'
    ]
  },
  {
    category: 'Exécution Scénario 3/4',
    items: [
      'Calcul débalancement',
      'Mécanisme compensatoire signé',
      'Impacts NEQ / opérations',
      'Plan cash-flow 2026–2028'
    ]
  }
];


// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface CorporateRestructuringCAcardProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CorporateRestructuringCAcard({
  isOpen,
  onClose
}: CorporateRestructuringCAcardProps) {
  const [activeTab, setActiveTab] = useState('resume');

  const tabs = [
    { id: 'resume', label: 'Résumé exécutif', icon: FileText },
    { id: 'scenarios', label: 'Scénarios DFK', icon: Target },
    { id: 'impacts', label: 'Impacts par actionnaire', icon: Users },
    { id: 'graphiques', label: 'Graphiques', icon: BarChart3 },
    { id: 'pdf', label: 'Document PDF', icon: FileText },
    { id: 'next', label: 'Prochaines briques', icon: ChevronRight }
  ];


  // Chart data
  const ownershipData = STAKEHOLDERS.map(s => ({
    name: s.name,
    value: s.ownership
  }));

  const impactData = STAKEHOLDERS.map(s => ({
    name: s.name,
    scenario1: s.impactStrength.scenario1,
    scenario2: s.impactStrength.scenario2,
    scenario3: s.impactStrength.scenario3,
    scenario4: s.impactStrength.scenario4
  }));

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];



  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[98vw] h-[95vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    Restructuration Corporative BZ - Analyse Décisionnelle
                  </h2>
                  <p className="text-amber-100 text-sm">
                    Document DFK • Analyse complète des scénarios • Aide à la décision
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex overflow-x-auto mt-4 gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'bg-white text-amber-600 shadow-lg'
                          : 'bg-white/20 text-white hover:bg-white/30'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'resume' && <ResumeTab />}
                  {activeTab === 'scenarios' && <ScenariosTab scenarios={SCENARIOS} />}
                  {activeTab === 'impacts' && <ImpactsTab stakeholders={STAKEHOLDERS} />}
                  {activeTab === 'graphiques' && <GraphiquesTab ownershipData={ownershipData} impactData={impactData} colors={COLORS} />}
                  {activeTab === 'pdf' && <PDFTab />}
                  {activeTab === 'next' && <NextStepsTab />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// TAB COMPONENTS
// ============================================================================

function ResumeTab() {
  return (
    <div className="space-y-6">
      {/* Hypothèses & Limites */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-amber-900 mb-2">Hypothèses & Limites</h3>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• Vente prévue en <strong>2028</strong></li>
              <li>• Dette estimée: <strong>~3.1M$</strong></li>
              <li>• Taux d'intérêt: <strong>5%</strong></li>
              <li>• Impacts estimatifs basés sur pourcentages d'actionnariat post-2026</li>
              <li>• Calculs approximatifs - validation fiscale requise</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Décision Recommandée */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-bold text-green-900 mb-2 text-xl">Décision Recommandée</h3>
            <p className="text-green-800 mb-4 font-semibold">
              <strong>Scénario 2 : Statut quo + Optimisation individuelle</strong>
            </p>
            <p className="text-green-700 mb-4">
              Ce scénario offre le meilleur équilibre entre optimisation fiscale et simplicité. 
              Il permet à chaque actionnaire d'optimiser individuellement sans restructuration majeure.
            </p>
            <div className="bg-white/60 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-green-900 mb-2">⚠️ Si scénario 3 ou 4 retenu :</p>
              <p className="text-sm text-green-800">
                Nécessite <strong>mécanisme compensatoire</strong> pour Dany (salaire/bonus/dividende prioritaire/clause neutralité).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Takeaways */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-600" />
          Key Takeaways
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Scénario 2 recommandé</p>
                <p className="text-sm text-gray-600">Optimisation individuelle sans restructuration majeure</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Scénarios 3/4 : Débalancement Dany</p>
                <p className="text-sm text-gray-600">~36K/an d'intérêts supportés (31.49% de 155K)</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Gagnants scénarios 3/4</p>
                <p className="text-sm text-gray-600">Yannick, Delisle, Octet, Vincent, Jérôme, JP</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Perdants scénarios 3/4</p>
                <p className="text-sm text-gray-600">Dany (fort), Zicat (léger)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mécanismes Compensatoires */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Mécanismes Compensatoires (si scénario 3/4)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COMPENSATION_MECHANISMS.map((mechanism) => (
            <div key={mechanism.id} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">{mechanism.title}</h4>
              <p className="text-sm text-gray-600 mb-3">{mechanism.description}</p>
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-1">Avantages:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {mechanism.pros.map((pro, idx) => (
                      <li key={idx}>• {pro}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">Désavantages:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {mechanism.cons.map((con, idx) => (
                      <li key={idx}>• {con}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScenariosTab({ scenarios }: { scenarios: Scenario[] }) {
  return (
    <div className="space-y-6">
      {scenarios.map((scenario, idx) => (
        <div key={scenario.id} className="bg-white rounded-xl shadow-md p-6 border-l-4 border-amber-500">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {idx + 1}. {scenario.title}
              </h3>
              <p className="text-gray-600">{scenario.description}</p>
            </div>
            <div className="flex gap-2">
              {scenario.winners.length > 0 && scenario.winners[0] !== 'Tous (neutre)' && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                  Gagnants: {scenario.winners.length}
                </span>
              )}
              {scenario.losers.length > 0 && scenario.losers[0] !== 'Aucun' && scenario.losers[0] !== 'Aucun (neutre)' && (
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                  Perdants: {scenario.losers.length}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-600" />
                Objectif
              </h4>
              <p className="text-sm text-gray-700 mb-4">{scenario.objective}</p>

              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Avantages
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 mb-4">
                {scenario.advantages.map((adv, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span>{adv}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-600" />
                Implications
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 mb-4">
                {scenario.implications.map((impl, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <span>{impl}</span>
                  </li>
                ))}
              </ul>

              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                Désavantages
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 mb-4">
                {scenario.disadvantages.map((dis, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">✗</span>
                    <span>{dis}</span>
                  </li>
                ))}
              </ul>

              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Risques
              </h4>
              <ul className="text-sm text-gray-700 space-y-1">
                {scenario.risks.map((risk, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-600 mt-1">⚠</span>
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ImpactsTab({ stakeholders }: { stakeholders: Stakeholder[] }) {
  // Calculs financiers
  const debt = 3100000; // ~3.1M
  const interestRate = 0.05; // 5%
  const annualInterest = debt * interestRate; // 155K/an
  const years = 3.5; // 3-4 ans (moyenne)

  // Calculer l'impact financier par actionnaire pour scénarios 3/4
  const financialImpacts = stakeholders.map(stakeholder => {
    const ownership = stakeholder.ownership / 100;
    const annualInterestShare = annualInterest * ownership;
    
    // Pour scénarios 3/4, Dany et Zicat supportent les intérêts (négatif)
    // Les autres bénéficient (positif via réduction de leur part)
    let impactAnnual = 0;
    if (stakeholder.impact.scenario3 === 'negative') {
      // Perdants : supportent les intérêts proportionnellement
      impactAnnual = -annualInterestShare;
    } else if (stakeholder.impact.scenario3 === 'positive') {
      // Gagnants : bénéficient d'une réduction (estimation simplifiée)
      // On estime qu'ils économisent une partie des intérêts qu'ils auraient supportés
      impactAnnual = annualInterestShare * 0.3; // Estimation : 30% de gain
    }

    const totalImpact = impactAnnual * years;
    const averageImpact = impactAnnual;

    return {
      stakeholder,
      annualInterestShare,
      impactAnnual,
      totalImpact,
      averageImpact
    };
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Impacts par Actionnaire (post-2026)</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Actionnaire</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">% Ownership</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Scénario 1</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Scénario 2</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Scénario 3</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Scénario 4</th>
              </tr>
            </thead>
            <tbody>
              {stakeholders.map((stakeholder) => (
                <tr key={stakeholder.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{stakeholder.name}</td>
                  <td className="py-3 px-4 text-center text-gray-700">{stakeholder.ownership}%</td>
                  {(['scenario1', 'scenario2', 'scenario3', 'scenario4'] as const).map((scenario) => {
                    const impact = stakeholder.impact[scenario];
                    const strength = stakeholder.impactStrength[scenario];
                    return (
                      <td key={scenario} className="py-3 px-4 text-center">
                        {impact === 'positive' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            <TrendingUp className="w-3 h-3" />
                            +{strength}
                          </span>
                        )}
                        {impact === 'negative' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                            <TrendingUp className="w-3 h-3 rotate-180" />
                            {strength}
                          </span>
                        )}
                        {impact === 'neutral' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                            <Minus className="w-3 h-3" />
                            0
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gagnants/Perdants Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-green-50 border-l-4 border-green-500 rounded-xl p-6">
          <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Gagnants (Scénarios 3/4)
          </h4>
          <ul className="space-y-2">
            {stakeholders
              .filter(s => s.impact.scenario3 === 'positive')
              .map(s => (
                <li key={s.id} className="text-sm text-green-800">
                  • {s.name} ({s.ownership}%)
                </li>
              ))}
          </ul>
        </div>
        <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
          <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            Perdants (Scénarios 3/4)
          </h4>
          <ul className="space-y-2">
            {stakeholders
              .filter(s => s.impact.scenario3 === 'negative')
              .map(s => (
                <li key={s.id} className="text-sm text-red-800">
                  • {s.name} ({s.ownership}%) - Impact: {s.impactStrength.scenario3 === -3 ? 'FORT' : 'LÉGER'}
                </li>
              ))}
          </ul>
        </div>
      </div>

      {/* Impact Financier - Scénarios 3/4 */}
      <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-amber-500">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-600" />
          Impact Financier (Scénarios 3/4) - Prochaines 3-4 années
        </h3>
        <div className="mb-4 p-4 bg-amber-50 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Hypothèses :</strong> Dette ~3.1M$, Taux d'intérêt 5% (155K$/an), Période 3.5 ans (moyenne 3-4 ans)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Actionnaire</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">% Ownership</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Part Intérêts Annuels</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Impact Annuel Moyen</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Impact Total (3.5 ans)</th>
              </tr>
            </thead>
            <tbody>
              {financialImpacts.map(({ stakeholder, annualInterestShare, impactAnnual, totalImpact }) => (
                <tr key={stakeholder.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{stakeholder.name}</td>
                  <td className="py-3 px-4 text-center text-gray-700">{stakeholder.ownership}%</td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    {annualInterestShare.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {impactAnnual !== 0 ? (
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                        impactAnnual > 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {impactAnnual > 0 ? '+' : ''}
                        {impactAnnual.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {totalImpact !== 0 ? (
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                        totalImpact > 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {totalImpact > 0 ? '+' : ''}
                        {totalImpact.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-bold">
                <td colSpan={2} className="py-3 px-4 text-gray-900">TOTAL</td>
                <td className="py-3 px-4 text-center text-gray-700">
                  {annualInterest.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}
                </td>
                <td className="py-3 px-4 text-center text-gray-700">
                  {financialImpacts.reduce((sum, f) => sum + f.impactAnnual, 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}
                </td>
                <td className="py-3 px-4 text-center text-gray-700">
                  {financialImpacts.reduce((sum, f) => sum + f.totalImpact, 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note :</strong> Les impacts positifs pour les gagnants sont des estimations basées sur la réduction de leur part proportionnelle des intérêts. 
            Les valeurs négatives représentent les intérêts supportés directement par les actionnaires.
          </p>
        </div>
      </div>
    </div>
  );
}

function GraphiquesTab({
  ownershipData,
  impactData,
  colors
}: {
  ownershipData: { name: string; value: number }[];
  impactData: { name: string; scenario1: number; scenario2: number; scenario3: number; scenario4: number }[];
  colors: string[];
}) {
  return (
    <div className="space-y-8">
      {/* Pie Chart - Ownership */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Ownership Post-2026</h3>
        <ResponsiveContainer width="100%" height={400}>
          <RechartsPieChart>
            <Pie
              data={ownershipData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(1) : 0}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {ownershipData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart - Impact Net */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Impact Net Estimé par Actionnaire</h3>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={impactData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="scenario1" fill={colors[0]} name="Scénario 1" />
            <Bar dataKey="scenario2" fill={colors[1]} name="Scénario 2" />
            <Bar dataKey="scenario3" fill={colors[2]} name="Scénario 3" />
            <Bar dataKey="scenario4" fill={colors[3]} name="Scénario 4" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Decision Matrix */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Matrice Décisionnelle</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Scénario</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Simplicité / Coûts Admin</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Équité / Neutralité Dany</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Risque Fiscal / Complexité</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium">Scénario 1</td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Élevé</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Élevé</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Moyen</span>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium">Scénario 2</td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Élevé</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Élevé</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Faible</span>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium">Scénario 3</td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Moyen</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Faible</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Moyen</span>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">Scénario 4</td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Moyen</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Faible</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Moyen</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PDFTab() {
  const pdfPath = '/docs/Simplification_structure_corporative_BZ.pdf';
  
  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">Document PDF - Restructuration Corporative BZ</h3>
          <p className="text-sm text-gray-600">Document DFK • 27 novembre 2025</p>
        </div>
        <a
          href={pdfPath}
          download="Simplification_structure_corporative_BZ.pdf"
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-md"
        >
          <FileDown className="w-4 h-4" />
          Télécharger le PDF
        </a>
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
        <iframe
          src={`${pdfPath}#toolbar=1&navpanes=1&scrollbar=1`}
          className="w-full h-[calc(95vh-250px)] min-h-[600px]"
          title="PDF Viewer"
        />
      </div>
      <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-lg">
        <p className="text-sm text-amber-800">
          <strong>Note :</strong> Si le PDF ne s'affiche pas correctement, utilisez le bouton "Télécharger le PDF" pour l'ouvrir dans votre lecteur PDF préféré.
        </p>
      </div>
    </div>
  );
}

function NextStepsTab() {
  return (
    <div className="space-y-6">
      {NEXT_STEPS.map((category, idx) => (
        <div key={idx} className="bg-white rounded-xl shadow-md p-6 border-l-4 border-amber-500">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ChevronRight className="w-5 h-5 text-amber-600" />
            {category.category}
          </h3>
          <ul className="space-y-2">
            {category.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700">
                <span className="text-amber-600 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

